import { InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { FAQPage, Product, Question, WithContext } from 'schema-dts';

import { Locale } from '../../../../core/i18n/Locale';
import {
    useExperimentVariation,
    useTrackPageViewAmplitude,
} from '../../../../core/Tracking/Amplitude/Amplitude';
import { HeadbandMessage } from '../../../../design-system/components/headband/HeadbandMessage';
import {
    roundRatingForText,
    scaleRating,
} from '../../../../design-system/components/review/rating/ReviewRating';
import { WemapProvider } from '../../../../design-system/components/wemap/WemapProvider';
import {
    Breadcrumb,
    useFacetBreadcrumbItems,
} from '../../../../domains/Layout/Breadcrumb/Breadcrumb';
import { Footer } from '../../../../domains/Layout/Footer/Footer';
import { Head } from '../../../../domains/Layout/Head/Head';
import { useJsonLd } from '../../../../domains/Layout/Head/JsonLd';
import {
    imageLoaderForId,
    imageUrlForStaticCdn,
} from '../../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../../domains/Layout/PrimaryNav/PrimaryNav';
import { WhereToGoSection } from '../../../../domains/WhereToGo/WhereToGoSection/WhereToGoSection';
import { getServerSideProps } from '../../../../partials/facet/getServerSideProps';
import { Cover } from '../../../../partials/facet/partials/Cover/Cover';
import { Itineraries } from '../../../../partials/facet/partials/Itineraries';
import { QuestionAnswers } from '../../../../partials/facet/partials/QuestionAnswers';
import { SuggestedFacets } from '../../../../partials/facet/partials/SuggestedFacets';
import { WhereToGoSectionDescription } from '../../../../partials/facet/partials/WhereToGoSectionDescription/WhereToGoSectionDescription';
import { PointOfInterestCarousel } from '../../../../partials/shared/components/PointOfInterestCarousel/PointOfInterestCarousel';
import { sortedAgenciesByBetterTripIndex } from '../../../../resources/agency/domain/operations/sortedAgenciesByBetterTripIndex';
import { Destination } from '../../../../resources/destination/domain/types/destination';
import { Facet } from '../../../../resources/facet/domain/types/facet';
import { FacetContents } from '../../../../resources/facetContents/domain/types/facetContents';
import { mapItineraryCardTagsToTotalCounts } from '../../../../resources/itinerary/domain/operations/getAmplitudeTags';
import { AverageRating } from '../../../../resources/rating/domain/types/averageRating';
import { AgencyListSection } from '../../../../shared/components/AgencyListSection/AgencyListSection';
import { COVER_HEIGHT } from '../../../../shared/components/Cover/Cover';
import FamilyMiniBookSection from '../../../../shared/components/FamilyMiniBookSection/FamilyMiniBookSection';
import { FamilyReassuranceStats } from '../../../../shared/components/FamilyReassurance/FamilyReassuranceStats/FamilyReassuranceStats';
import { OtherDestinationsSection } from '../../../../shared/components/OtherDestinationsSection/OtherDestinationSection';
import { RegionCarouselSection } from '../../../../shared/components/RegionCarouselSection/RegionCarouselSection';
import { TripReviewListDestinationSection } from '../../../../shared/components/TripReviewListDestinationSection/TripReviewListDestinationSection';
import { UGCHighlightSection } from '../../../../shared/components/UGCHighlightSection/UGCHighlightSection';
import { WhenToGoSection } from '../../../../shared/components/WhenToGoSection/WhenToGoSection';
import { useFeatureFlag } from '../../../../shared/hooks/useFeatureFlag';
import { useLocale } from '../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../shared/hooks/useUrlGenerator';
import { useCurrentSite } from '../../../../shared/providers/currentSite/useCurrentSite';
import { generateUrl } from '../../../../shared/utilities/routing/routing';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';

export { getServerSideProps };

export type PageFacetProps = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function PageFacet(props: PageFacetProps) {
    const router = useRouter();
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const { currentSite } = useCurrentSite();
    const { generateUrl } = useUrlGenerator();
    const { hasFeature } = useFeatureFlag();

    const {
        facet,
        facetContents,
        primaryNav,
        itineraries,
        tripReviews,
        hrefLangs,
        suggestedFacets,
        destinationItinerariesCount,
        regions,
        destinationsTypeRegion,
        otherDestinations,
        averageRating,
        ugcHighlights,
        mainWhenToGo,
        pointsOfInterest,
    } = props.data;
    const { destination, metaTitle } = facet;
    const { breadcrumbItems } = useFacetBreadcrumbItems({ destination, facet });
    const metaDescription = facet.metaDescription.replace('{nb_iti}', '10');
    const coverImageId = parseInt(facet?.coverImage?.id || destination.coverPicture.id, 10);
    const coverCopyright = facet?.coverImage?.author || destination.coverPicture.author;
    const agencies = sortedAgenciesByBetterTripIndex(
        itineraries
            .map((itinerary) => {
                return itinerary.agency;
            })
            .reduce<PageFacetProps['data']['itineraries'][0]['agency'][]>(
                (uniqueAgencies, agency) => {
                    if (!uniqueAgencies.find(({ id }) => id === agency.id)) {
                        uniqueAgencies.push(agency);
                    }

                    return uniqueAgencies;
                },
                [],
            ),
    );
    const highlightedRegions = regions.filter((region) => {
        return region.isHighlighted;
    });

    const whenToGoTitle =
        facetContents.whenToGo?.title ??
        formatMessage(
            { id: 'page.facet.whenToGo.title' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        );

    const tripReviewsTitle = {
        as: 'div',
        children: formatMessage({ id: 'global.component.tripReviewListSection.subTitle' }),
    } as const;

    const tripReviewsSubTitle = {
        as: 'div',
        scale: 'primary-l-extra-bold',
        children: formatMessage(
            { id: 'global.component.tripReviewListSection.title' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        ),
    } as const;

    const whereToGoTitle = facetContents.whereToGo
        ? facetContents.whereToGo.title
        : facetContents.mainContent?.title;
    const whereToGoContent = facetContents.whereToGo
        ? facetContents.whereToGo.content
        : facetContents.mainContent?.content;
    const whereToGoContentReferences = facetContents.whereToGo
        ? facetContents.whereToGo.contentReferences
        : facetContents.mainContent?.contentReferences;

    const jsonLd = useJsonLd([
        getFacetJsonLd({
            averageRating,
            description: metaDescription,
            destination,
            facet,
            imageId: coverImageId,
            locale,
            name: facet.title,
        }),
        getQuestionAnswersJsonLd(facetContents, {
            whenToGoTitleFallback: formatMessage(
                { id: 'page.facet.whenToGo.title' },
                {
                    destinationPreposition: destination.preposition,
                    destinationName: destination.name,
                },
            ),
        }),
    ]);

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        ampli.viewedPageFacet({
            ...defaultProperties,
            destination_id: destination.id,
            destination_slug: destination.slug,
            has_trip_form_available: destination.isAvailableForQuoteRequest,
            facet_id: facet.id,
            facet_tag_category: facet.filter.name,
            facet_tag_name:
                typeof facet.filter.value === 'boolean' ? facet.filter.name : facet.filter.value,
            nb_itineraries_available: itineraries.length,
            nb_questions_displayed: facetContents.questionAnswers.length,
            has_alert_message: Boolean(destination.alertMessage),
            nb_tags_list: mapItineraryCardTagsToTotalCounts(itineraries),
        });
    });

    const ABPathfinderExperimentFacetTripDesigner = useExperimentVariation(
        'fr-x-pathfinder-facet-trip-designer',
        {
            destination_slug: destination.slug,
            facet_tag_name:
                typeof facet.filter.value === 'boolean' ? facet.filter.name : facet.filter.value,
        },
    );
    const isABPathfinderExperimentFacetTripDesigner =
        ABPathfinderExperimentFacetTripDesigner?.value === 'variation';

    const isFamilyFacet = facet.filter.name === 'profile' && facet.filter.value === 'family';
    const showFamilyMiniBookSection = isFamilyFacet && hasFeature('family_minibook');
    const [localePrefix] = locale.split('-');
    const familyMiniBookTagSource = `${localePrefix}_minibookfamily_2025_facetpage`;

    return (
        <>
            <Head
                title={metaTitle}
                description={metaDescription}
                canonical={generateUrl('facet', {
                    destinationSlug: destination.slug,
                    facetSlug: facet.slug,
                })}
                hrefLangs={hrefLangs}
                og={{
                    title: metaTitle,
                    description: metaDescription,
                    image: imageLoaderForId({
                        aspectRatio: 1.91,
                    })({
                        src: coverImageId.toString(),
                        width: 1200,
                    }),
                    locale,
                    url: currentSite.url + router.asPath,
                    type: 'website',
                    site_name: currentSite.organisation.name,
                }}
                jsonLd={jsonLd}
            />

            {destination.alertMessage ? (
                <HeadbandMessage message={destination.alertMessage} />
            ) : null}

            <PrimaryNav contextContinent={destination.continent} {...primaryNav} />

            <div className="layout-with-centered-content mb-72 md:mb-112">
                <section className="main-container-cover mb-40">
                    <Cover
                        image={{
                            alt: '',
                            src: coverImageId.toString(),
                            loader: imageLoaderForId({
                                fixedHeight: COVER_HEIGHT,
                            }),
                            quality: 80,
                            sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 50vw',
                        }}
                        copyright={coverCopyright}
                        facet={facet}
                        destination={facet.destination}
                        trackingInformation={{
                            tripFormContactAgency: {
                                block: 'cartouche',
                                facet_id: facet.id,
                                destination_id: destination.id,
                                destination_slug: destination.slug,
                            },
                        }}
                        destinationHasItinerary={destinationItinerariesCount > 0}
                        isFamilyFacet={isFamilyFacet}
                    />
                </section>

                <section className="main-container mb-32">
                    <Breadcrumb items={breadcrumbItems} />
                </section>

                {itineraries.length > 0 ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <Itineraries
                            itineraries={itineraries}
                            destination={destination}
                            destinationItineraryCount={destinationItinerariesCount}
                            facetContents={facetContents}
                            facet={facet}
                            agencyName={(agency) =>
                                formatMessage(
                                    { id: 'global.component.itineraryCard.mainAgentName' },
                                    { name: agency.name },
                                )
                            }
                            isABTripDesigner={isABPathfinderExperimentFacetTripDesigner}
                        />
                    </section>
                ) : null}

                {isFamilyFacet ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <FamilyReassuranceStats showLogo={true} />
                    </section>
                ) : null}
            </div>

            {agencies.length > 0 ? (
                <div className="bg-beige-100">
                    <div className="layout-with-centered-content mb-40 mt-40 py-40 lg:mb-96 lg:mt-0 lg:py-56">
                        <section className="main-container">
                            <AgencyListSection
                                destination={destination}
                                agencies={agencies}
                                subTitleElement="div"
                                agencyName={(agency) =>
                                    formatMessage(
                                        { id: 'global.component.agencyCard.teamName' },
                                        { name: agency.name },
                                    )
                                }
                            />
                        </section>
                    </div>
                </div>
            ) : null}

            {showFamilyMiniBookSection ? (
                <div className="layout-with-centered-content mb-40 mt-40 py-40 lg:mb-96 lg:mt-0 lg:py-56">
                    <section className="main-container">
                        <FamilyMiniBookSection
                            destination={destination}
                            tagSource={familyMiniBookTagSource}
                        />
                    </section>
                </div>
            ) : null}

            {destinationsTypeRegion.length > 0 || highlightedRegions.length > 0 ? (
                <div className="mb-40 mt-40 last:mb-0 lg:mb-96 lg:mt-96 lg:hidden">
                    <div className="layout-with-centered-content">
                        <section className="main-container">
                            <RegionCarouselSection
                                title={formatMessage(
                                    { id: 'page.destination.regions.title' },
                                    {
                                        destinationPreposition: destination.preposition,
                                        destinationName: destination.name,
                                    },
                                )}
                                destinationsTypeRegion={destinationsTypeRegion}
                                regions={highlightedRegions}
                                titleElement="div"
                                regionNameElement="span"
                            />
                        </section>
                    </div>
                </div>
            ) : null}

            <div className="layout-with-centered-content mb-40 last:mb-0 lg:mb-96">
                {whereToGoTitle && whereToGoContent && whereToGoContentReferences ? (
                    <>
                        <WemapProvider>
                            <WhereToGoSection
                                pointsOfInterest={whereToGoContentReferences.pointOfInterests}
                                regions={regions}
                                destination={destination}
                                destinationsTypeRegion={destinationsTypeRegion}
                                secondaryRegionTitleElement="div"
                                regionNameElement="span"
                            />
                        </WemapProvider>

                        <section className="main-container mb-40 mt-40 last:mb-0 md:mb-56 lg:mt-96 xl:mb-96">
                            <div className="row">
                                <div className="col-12 col-lg-6">
                                    <WhereToGoSectionDescription
                                        title={whereToGoTitle}
                                        content={whereToGoContent}
                                    />
                                </div>
                            </div>
                        </section>
                    </>
                ) : null}

                {pointsOfInterest.length > 0 ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <PointOfInterestCarousel pointsOfInterest={pointsOfInterest} />
                    </section>
                ) : null}

                {ugcHighlights.length > 0 ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <UGCHighlightSection
                            ugcHighlights={ugcHighlights}
                            destination={destination}
                        />
                    </section>
                ) : null}
            </div>

            {mainWhenToGo && facetContents.whenToGo ? (
                <div className="layout-with-centered-content mb-40 mt-40 lg:mb-96 lg:mt-0">
                    <section className="main-container mb-96 last:mb-0">
                        <WhenToGoSection
                            destination={destination}
                            content={facetContents.whenToGo.content}
                            title={whenToGoTitle}
                            subTitleElement="h2"
                            cta={{
                                title: whenToGoTitle,
                                label: formatMessage({ id: 'global.cta.seeWhenToGo' }),
                                url: generateUrl('destination_when_to_go', {
                                    destinationSlug: destination.slug,
                                }),
                            }}
                        />
                    </section>
                </div>
            ) : null}

            <div className="layout-with-centered-content mb-40 last:mb-0 lg:mb-96">
                {facetContents.questionAnswers.length > 0 ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <QuestionAnswers questionAnswers={facetContents.questionAnswers} />
                    </section>
                ) : null}

                {tripReviews.length > 0 && averageRating ? (
                    <section
                        id="reviews"
                        className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96"
                    >
                        <TripReviewListDestinationSection
                            averageRating={averageRating}
                            destination={destination}
                            tripReviews={tripReviews}
                            title={tripReviewsTitle}
                            subtitle={tripReviewsSubTitle}
                        />
                    </section>
                ) : null}

                {suggestedFacets.length > 0 ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <SuggestedFacets
                            suggestedFacets={suggestedFacets}
                            destination={destination}
                        />
                    </section>
                ) : null}

                {otherDestinations.length > 0 ? (
                    <section className="main-container mb-40 last:mb-0 md:mb-56 xl:mb-96">
                        <OtherDestinationsSection
                            destinations={otherDestinations}
                            title={facetContents.otherDestinationsSectionTitle ?? undefined}
                        />
                    </section>
                ) : null}
            </div>

            <Footer
                destination={destination}
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
        </>
    );
}

function getFacetJsonLd({
    averageRating,
    description,
    destination,
    facet,
    imageId,
    locale,
    name,
}: {
    averageRating: AverageRating;
    description: string;
    destination: PickDomain<Destination, 'slug'>;
    facet: PickDomain<Facet, 'slug'>;
    imageId: number;
    locale: Locale;
    name: string;
}): WithContext<Product> | null {
    if (averageRating.count === 0) {
        return null;
    }

    const rating = scaleRating(averageRating.rating, 0, 10);

    return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: name,
        description: description,
        image: [
            imageUrlForStaticCdn(imageId, 400, 400),
            imageUrlForStaticCdn(imageId, 400, 300),
            imageUrlForStaticCdn(imageId, 800, 450),
        ],
        url: generateUrl('facet', locale, {
            destinationSlug: destination.slug,
            facetSlug: facet.slug,
        }),
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: roundRatingForText(rating.value),
            ratingCount: averageRating.count,
            bestRating: rating.max,
            worstRating: rating.min,
        },
        brand: 'Evaneos',
    };
}

function getQuestionAnswersJsonLd(
    facetContents: PickDomain<
        FacetContents,
        'questionAnswers',
        {
            whereToGo: {
                content: FacetContents['whereToGo']['content'];
                title: FacetContents['whereToGo']['title'];
            } | null;
            whenToGo: {
                content: FacetContents['whenToGo']['content'];
                title: FacetContents['whenToGo']['title'] | null;
            } | null;
        }
    >,
    {
        whenToGoTitleFallback,
    }: {
        whenToGoTitleFallback: string;
    },
): WithContext<FAQPage> | null {
    const { questionAnswers, whereToGo, whenToGo } = facetContents;
    const questions: Question[] = [];

    if (whereToGo) {
        questions.push({
            '@type': 'Question',
            name: whereToGo.title,
            acceptedAnswer: {
                '@type': 'Answer',
                text: whereToGo.content,
            },
        });
    }

    if (whenToGo) {
        questions.push({
            '@type': 'Question',
            name: whenToGo.title ?? whenToGoTitleFallback,
            acceptedAnswer: {
                '@type': 'Answer',
                text: whenToGo.content,
            },
        });
    }

    questionAnswers.forEach((questionAnswer) => {
        questions.push({
            '@type': 'Question',
            name: questionAnswer.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: questionAnswer.answer,
            },
        });
    });

    if (questions.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions,
    };
}
